import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import OrderDetailPanel from '../components/OrderDetailPanel';
import type { Order } from '../components/orderDetailTypes';

function unwrapOrder(res: { data?: unknown }): Order | null {
  const d = res.data as Record<string, unknown> | undefined;
  if (!d) return null;
  if (d.data != null && typeof d.data === 'object') return d.data as Order;
  return d as unknown as Order;
}

export default function AdminOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const res = await api.get(`/orders/${orderId}`);
      const o = unwrapOrder(res);
      setOrder(o);
    } catch (e) {
      console.error(e);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const reloadOrder = useCallback(async (): Promise<Order | null> => {
    if (!orderId) return null;
    try {
      const res = await api.get(`/orders/${orderId}`);
      const o = unwrapOrder(res);
      if (o) setOrder(o);
      return o;
    } catch {
      return null;
    }
  }, [orderId]);

  if (loading && !order) {
    return (
      <DashboardLayout title={t('orders.details')}>
        <div className="text-center py-16 text-gray-600">{t('orders.loading')}</div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout title={t('orders.details')}>
        <p className="text-red-600">{t('orders.noOrders')}</p>
        <button type="button" className="mt-4 text-primary-orange underline" onClick={() => navigate(-1)}>
          {t('common.goBack')}
        </button>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('orders.details')}>
      <div className={`max-w-2xl mx-auto space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
        <button type="button" onClick={() => navigate(-1)} className="text-sm text-primary-orange hover:underline">
          ← {t('common.goBack')}
        </button>
        <div className="bg-white rounded-xl shadow-md p-5">
          <OrderDetailPanel order={order} replaceOrder={setOrder} reloadOrder={reloadOrder} />
        </div>
      </div>
    </DashboardLayout>
  );
}
